import React, { useCallback, useEffect, useRef, useState } from "react"

import { Button, Input } from "@coval/ui"
import { ChevronDown, ChevronUp, Replace, Search, X } from "lucide-react"

import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import { getScriptTurnText, setScriptTurnText } from "@/types/ScriptTurn"

import { useFindReplaceContext } from "../contexts/FindReplaceContext"
import { useTestSetEditorContext } from "../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../event-bus"
import type { EditableTestCase } from "../model"
import {
  findTestCaseMatches,
  type FindReplaceMatch,
} from "../utils/findReplaceMatches"

interface FindReplaceProps {
  isOpen: boolean
  onClose: () => void
  initialMode?: "find" | "replace"
}

export const FindReplace: React.FC<FindReplaceProps> = ({
  isOpen,
  onClose,
  initialMode = "find",
}) => {
  const { testCases } = useTestSetEditorContext()
  const { setFindReplaceState } = useFindReplaceContext()
  const dispatch = useEventDispatcher()
  const [mode, setMode] = useState<"find" | "replace">(initialMode)
  const [findText, setFindText] = useState("")
  const [replaceText, setReplaceText] = useState("")
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)
  const [matches, setMatches] = useState<FindReplaceMatch[]>([])
  const findInputRef = useRef<HTMLInputElement>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)
  const wasOpenRef = useRef(false)

  const findMatches = useCallback(
    (searchText: string): FindReplaceMatch[] =>
      findTestCaseMatches(testCases, searchText),
    [testCases],
  )

  // Update matches when find text changes
  useEffect(() => {
    if (findText.trim()) {
      const newMatches = findMatches(findText)
      setMatches(newMatches)
      setCurrentMatchIndex(0)
      // Update context for highlighting
      setFindReplaceState({
        findText,
        currentMatchIndex: 0,
        matches: newMatches.map((m) => ({
          testCaseId: m.testCaseId,
          field: m.field,
          fieldKey: m.fieldKey,
        })),
      })
    } else {
      setMatches([])
      setCurrentMatchIndex(0)
      setFindReplaceState(null)
    }
  }, [findText, findMatches, setFindReplaceState])

  // Focus find input and expand all test cases when opened
  useEffect(() => {
    if (isOpen) {
      // Expand all test cases so matches are visible
      dispatch(EVENTS.EXPAND_ALL, {})

      // Focus find input
      if (findInputRef.current) {
        findInputRef.current.focus()
        findInputRef.current.select()
      }
    } else {
      // Clear highlighting when closed
      setFindReplaceState(null)

      // Restore the collapsed view we forced open, so users aren't left
      // with every row expanded and no bulk-collapse control
      if (wasOpenRef.current) {
        dispatch(EVENTS.COLLAPSE_ALL, {})
      }
    }

    wasOpenRef.current = isOpen
  }, [isOpen, dispatch, setFindReplaceState])

  const scrollToMatch = useCallback(
    (matchIndex: number) => {
      if (
        matches.length === 0 ||
        matchIndex < 0 ||
        matchIndex >= matches.length
      ) {
        return
      }

      const match = matches[matchIndex]
      // Scroll the test case into view
      const element = document.querySelector(
        `[data-test-case-id="${match.testCaseId}"]`,
      )
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    },
    [matches],
  )

  const findNext = useCallback(() => {
    if (matches.length === 0) {
      return
    }
    const newIndex = (currentMatchIndex + 1) % matches.length
    setCurrentMatchIndex(newIndex)
    scrollToMatch(newIndex)
    // Update context
    if (findText.trim()) {
      setFindReplaceState({
        findText,
        currentMatchIndex: newIndex,
        matches: matches.map((m) => ({
          testCaseId: m.testCaseId,
          field: m.field,
          fieldKey: m.fieldKey,
        })),
      })
    }
  }, [matches, currentMatchIndex, findText, setFindReplaceState, scrollToMatch])

  const findPrevious = useCallback(() => {
    if (matches.length === 0) {
      return
    }
    const newIndex = (currentMatchIndex - 1 + matches.length) % matches.length
    setCurrentMatchIndex(newIndex)
    scrollToMatch(newIndex)
    // Update context
    if (findText.trim()) {
      setFindReplaceState({
        findText,
        currentMatchIndex: newIndex,
        matches: matches.map((m) => ({
          testCaseId: m.testCaseId,
          field: m.field,
          fieldKey: m.fieldKey,
        })),
      })
    }
  }, [matches, currentMatchIndex, findText, setFindReplaceState, scrollToMatch])

  const parseFieldKey = useCallback((rawFieldKey: string) => {
    const lastColon = rawFieldKey.lastIndexOf(":")
    if (lastColon === -1) {
      return { key: rawFieldKey, subfield: "" }
    }
    return {
      key: rawFieldKey.slice(0, lastColon),
      subfield: rawFieldKey.slice(lastColon + 1),
    }
  }, [])

  const replaceCurrent = useCallback(() => {
    if (matches.length === 0 || currentMatchIndex >= matches.length) {
      return
    }

    const match = matches[currentMatchIndex]
    const testCase = testCases.find((tc) => tc.id === match.testCaseId)
    if (!testCase) {
      return
    }

    // Use findText to replace all matches in the current field (case-insensitive)
    const regex = new RegExp(
      findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
      "gi",
    )

    const updatedTestCase: Partial<EditableTestCase> = {}

    if (match.field === "input_str") {
      const newValue = testCase.input_str.replace(regex, replaceText)
      updatedTestCase.input_str = newValue
    } else if (
      match.field === "script_turns" &&
      match.fieldKey &&
      testCase.script_turns
    ) {
      const turnIndex = parseInt(match.fieldKey, 10)
      const turns = [...testCase.script_turns]
      const text = getScriptTurnText(turns[turnIndex])
      if (text !== null) {
        // Replace only the selected occurrence, located by its position in
        // the turn's current text — stored character offsets can go stale
        // between the dispatch and the match recompute.
        let occurrence = 0
        turns[turnIndex] = setScriptTurnText(
          turns[turnIndex],
          text.replace(regex, (matched) =>
            occurrence++ === match.matchIndex ? replaceText : matched,
          ),
        )
      }
      updatedTestCase.script_turns = turns
    } else if (match.field === "expected_behaviors" && match.fieldKey) {
      const behaviorIndex = parseInt(match.fieldKey, 10)
      const behaviors = [...testCase.expected_behaviors]
      behaviors[behaviorIndex] = behaviors[behaviorIndex].replace(
        regex,
        replaceText,
      )
      updatedTestCase.expected_behaviors = behaviors
    } else if (
      match.field === "expected_output_json" &&
      match.fieldKey &&
      testCase.expected_output_json
    ) {
      const { key, subfield } = parseFieldKey(match.fieldKey)
      const metadata = { ...testCase.expected_output_json }

      if (subfield === "key") {
        // Replace in key name
        const newKey = key.replace(regex, replaceText)
        if (newKey !== key) {
          metadata[newKey] = metadata[key]
          delete metadata[key]
        }
      } else if (subfield === "value") {
        // Replace in value
        const currentValue = metadata[key]
        if (typeof currentValue === "string") {
          metadata[key] = currentValue.replace(regex, replaceText)
        }
      }

      updatedTestCase.expected_output_json = metadata
    }

    // Dispatch update
    dispatch(EVENTS.TEST_CASE_UPDATE, {
      testCaseId: match.testCaseId,
      updates: updatedTestCase,
    })

    // Recalculate matches after replace
    const newMatches = findMatches(findText)
    const newCurrentIndex =
      newMatches.length === 0
        ? 0
        : Math.min(currentMatchIndex, newMatches.length - 1)
    setMatches(newMatches)
    setCurrentMatchIndex(newCurrentIndex)
    // Update context
    if (findText.trim()) {
      setFindReplaceState({
        findText,
        currentMatchIndex: newCurrentIndex,
        matches: newMatches.map((m) => ({
          testCaseId: m.testCaseId,
          field: m.field,
          fieldKey: m.fieldKey,
        })),
      })
    } else {
      setFindReplaceState(null)
    }
  }, [
    matches,
    currentMatchIndex,
    testCases,
    replaceText,
    findMatches,
    parseFieldKey,
    dispatch,
    findText,
    setFindReplaceState,
  ])

  const handleClose = useCallback(() => {
    setFindReplaceState(null)
    onClose()
  }, [onClose, setFindReplaceState])

  useEscapeShortcut({
    allowInInputs: true,
    description: "Close find and replace",
    enabled: isOpen,
    handler: handleClose,
    priority: ESCAPE_SHORTCUT_PRIORITY.DIALOG,
  })

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Enter finds next (if not in input)
      if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault()
        findPrevious()
        return
      }

      if (e.key === "Enter" && !e.shiftKey) {
        // If in replace mode and not in replace input, do replace
        if (
          mode === "replace" &&
          document.activeElement !== replaceInputRef.current &&
          matches.length > 0
        ) {
          e.preventDefault()
          replaceCurrent()
          return
        }
        // Otherwise find next
        if (document.activeElement === findInputRef.current) {
          e.preventDefault()
          findNext()
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [
    isOpen,
    mode,
    matches,
    currentMatchIndex,
    findNext,
    findPrevious,
    replaceCurrent,
    handleClose,
  ])

  const replaceAll = useCallback(() => {
    if (matches.length === 0) {
      return
    }

    // Group matches by test case and unique field location
    // This ensures we only replace once per field, not once per match
    const fieldGroups = new Map<string, Set<string>>() // testCaseId -> Set of field keys

    matches.forEach((match) => {
      const fieldKey = match.fieldKey
        ? `${match.field}:${match.fieldKey}`
        : match.field

      if (!fieldGroups.has(match.testCaseId)) {
        fieldGroups.set(match.testCaseId, new Set())
      }
      fieldGroups.get(match.testCaseId)!.add(fieldKey)
    })

    // Build updates map with initial state
    const updates = new Map<
      string,
      Partial<EditableTestCase> & { id: string }
    >()

    fieldGroups.forEach((fieldSet, testCaseId) => {
      const testCase = testCases.find((tc) => tc.id === testCaseId)
      if (!testCase) {
        return
      }

      updates.set(testCaseId, {
        id: testCaseId,
        input_str: testCase.input_str,
        expected_behaviors: [...testCase.expected_behaviors],
        expected_output_json: testCase.expected_output_json
          ? { ...testCase.expected_output_json }
          : null,
        script_turns: testCase.script_turns ? [...testCase.script_turns] : null,
      })

      const update = updates.get(testCaseId)!
      const regex = new RegExp(
        findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "gi",
      )

      // Process each unique field once
      fieldSet.forEach((fieldKey) => {
        if (fieldKey === "input_str" && update.input_str) {
          update.input_str = update.input_str.replace(regex, replaceText)
        } else if (fieldKey.startsWith("script_turns:")) {
          const turnIndex = parseInt(fieldKey.split(":")[1], 10)
          const turn = update.script_turns?.[turnIndex]
          const text = turn === undefined ? null : getScriptTurnText(turn)
          if (update.script_turns && turn !== undefined && text !== null) {
            update.script_turns[turnIndex] = setScriptTurnText(
              turn,
              text.replace(regex, replaceText),
            )
          }
        } else if (fieldKey.startsWith("expected_behaviors:")) {
          const behaviorIndex = parseInt(fieldKey.split(":")[1], 10)
          if (update.expected_behaviors?.[behaviorIndex]) {
            update.expected_behaviors[behaviorIndex] =
              update.expected_behaviors[behaviorIndex].replace(
                regex,
                replaceText,
              )
          }
        } else if (fieldKey.startsWith("expected_output_json:")) {
          const withoutPrefix = fieldKey.replace(/^expected_output_json:/, "")
          const { key, subfield } = parseFieldKey(withoutPrefix)
          if (!update.expected_output_json) {
            return
          }

          if (subfield === "key") {
            const newKey = key.replace(regex, replaceText)
            if (
              newKey !== key &&
              update.expected_output_json[key] !== undefined
            ) {
              update.expected_output_json[newKey] =
                update.expected_output_json[key]
              delete update.expected_output_json[key]
            }
          } else if (subfield === "value") {
            const currentValue = update.expected_output_json[key]
            if (typeof currentValue === "string") {
              update.expected_output_json[key] = currentValue.replace(
                regex,
                replaceText,
              )
            }
          }
        }
      })
    })

    // Apply all updates
    updates.forEach((update) => {
      const { id, ...updates } = update
      dispatch(EVENTS.TEST_CASE_UPDATE, {
        testCaseId: id,
        updates,
      })
    })

    // Clear matches
    setMatches([])
    setCurrentMatchIndex(0)
    setFindReplaceState(null)
  }, [
    matches,
    testCases,
    replaceText,
    dispatch,
    setFindReplaceState,
    findText,
    parseFieldKey,
  ])

  if (!isOpen) {
    return null
  }

  const matchCountText =
    matches.length > 0
      ? `${currentMatchIndex + 1} of ${matches.length}`
      : "No matches"

  return (
    <div className="absolute top-0 left-0 right-0 z-50 bg-surface-1 border-b border-coval-border shadow-lg">
      <div className="px-4 py-3 flex items-center gap-2">
        <div className="flex items-center gap-2 flex-1">
          <div className="flex items-center gap-2 flex-1">
            <Search className="w-4 h-4 text-coval-text-muted shrink-0" />
            <Input
              ref={findInputRef}
              aria-label="Find"
              placeholder="Find"
              value={findText}
              onChange={(e) => setFindText(e.target.value)}
              className="flex-1 max-w-xs h-8 text-sm"
            />
          </div>

          {mode === "replace" && (
            <div className="flex items-center gap-2 flex-1">
              <Replace className="w-4 h-4 text-coval-text-muted shrink-0" />
              <Input
                ref={replaceInputRef}
                aria-label="Replace"
                placeholder="Replace"
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
                className="flex-1 max-w-xs h-8 text-sm"
              />
            </div>
          )}

          <div className="flex items-center gap-1">
            <Button
              icon
              variant="ghost"
              onClick={findPrevious}
              disabled={matches.length === 0}
              tooltip="Find previous"
              className="min-w-8 h-8"
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
            <Button
              icon
              variant="ghost"
              onClick={findNext}
              disabled={matches.length === 0}
              tooltip="Find next"
              className="min-w-8 h-8"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>

          {mode === "replace" && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                onClick={replaceCurrent}
                disabled={matches.length === 0}
                className="h-8 text-xs"
              >
                Replace
              </Button>
              <Button
                variant="ghost"
                onClick={replaceAll}
                disabled={matches.length === 0}
                className="h-8 text-xs"
              >
                Replace All
              </Button>
            </div>
          )}

          <div className="text-xs text-coval-text-muted min-w-[80px] text-right">
            {matchCountText}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => setMode(mode === "find" ? "replace" : "find")}
            className="h-8 text-xs"
          >
            {mode === "find" ? "Replace" : "Find"}
          </Button>
          <Button
            icon
            variant="ghost"
            onClick={onClose}
            tooltip="Close"
            className="min-w-8 h-8"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
